import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import type { ThemeProps } from '@mutiny-pkg/champaign-schemas/src/schemas/flexiblePages/themeSchema';
import { champaignUrlDomain } from '@mutiny-pkg/config/src/domain';
import { type LoadedCustomComponentModule, registerHostRuntime } from './hostRuntime';
import { createCustomComponentLoader, type CustomComponentLoader } from './customComponentLoader';
import { ensureRuntimeLibrariesRegistered } from './runtimeLibraryModules';

export interface CustomComponentArtifactEntry {
  /** Editor-only — drives the design-sidebar attributes editor. Omitted on published pages. */
  propsSchema?: Record<string, unknown> | null;
}

export interface CustomComponentFilesystem {
  /**
   * Keyed by sourceHash. Stores per-artifact metadata (currently just propsSchema for the editor
   * sidebar). The renderer doesn't gate on entries being present here — `resolveCompiledUrl` is
   * the source of truth for whether bytes can be fetched for a given hash.
   */
  artifacts: Record<string, CustomComponentArtifactEntry>;
  /**
   * Slug → current canonical sourceHash for this page. Used by the artifact loader to resolve
   * `import X from 'components/<slug>'` imports inside a compiled artifact body. The outer node
   * pin always wins for what THIS node renders; this map only matters for transitive imports.
   */
  slugToCurrentSourceHash: Record<string, string>;
}

export interface CustomComponentRenderErrorPayload {
  /** `null` when a window error matches our blob URL set but no slug currently pins that sourceHash. */
  slug: string | null;
  sourceHash: string;
  errorName: string;
  errorMessage: string;
  stack?: string;
}

export interface CustomComponentContextValue {
  filesystem: CustomComponentFilesystem | null;
  theme: ThemeProps;
  overrideVariables: Record<string, string> | undefined;
  isEditor: boolean;
  loadModuleByHash: (sourceHash: string) => Promise<LoadedCustomComponentModule>;
  /**
   * Synchronous lookup for already-loaded modules. Server SSR fills this from the data loader's
   * pre-evaluation; the client mirrors it via Vike's `+onBeforeRenderClient` which re-fetches the
   * same artifacts before `hydrateRoot`. The first render of each slot then resolves `'ready'`
   * synchronously and matches the SSR'd DOM exactly — no hydration mismatch. Optional so test
   * harnesses that fake the provider can omit it; the hook treats absence as "no preload".
   */
  getPreloadedModule?: (sourceHash: string) => LoadedCustomComponentModule | null;
  onRenderError: ((payload: CustomComponentRenderErrorPayload) => void) | null;
}

export const CustomComponentContext = createContext<CustomComponentContextValue | null>(null);

interface CustomComponentProviderProps {
  filesystem: CustomComponentFilesystem | null;
  theme: ThemeProps;
  overrideVariables?: Record<string, string>;
  isEditor?: boolean;
  /**
   * Returns the URL to fetch the compiled bundle for a given `sourceHash`. The provider keeps the
   * URL strategy out-of-band (proxy in editor + preview, static-relative in published archives)
   * so the caller picks the strategy without baking it into the manifest. Renderers always call
   * this — they never need to look up `filesystem.artifacts[hash].compiledUrl`.
   */
  resolveCompiledUrl: (sourceHash: string) => string;
  /**
   * Returns the absolute URL to fetch the runtime shim for an `import` specifier baked into a
   * compiled artifact (always under `/__mutiny/runtime/`). Editor canvas points at champaign;
   * published pages point at the company's same-origin static CDN so the published page has no
   * runtime dependency on champaign and avoids the cross-origin CORS preflight that would fail
   * for customer domains. Defaults to a champaign-relative resolver for callers that haven't
   * been updated yet.
   */
  resolveRuntimeUrl?: (specifier: string) => string;
  /** Fired on React boundary catches + window errors/rejections from our blob URLs. Editor wires to agent; published pages leave unset. */
  onRenderError?: (payload: CustomComponentRenderErrorPayload) => void;
  /**
   * Pre-evaluated modules keyed by `sourceHash`. Set on the server by `+data.ts` so SSR emits
   * filled DOM, and on the client by `+onBeforeRenderClient` so hydration starts with the same
   * modules sync-available. Descendant `<CustomComponent>` slots resolve `'ready'` on the first
   * render whenever a hash is present here, matching SSR exactly and avoiding hydration mismatch.
   */
  preloadedModules?: ReadonlyMap<string, LoadedCustomComponentModule>;
  children: React.ReactNode;
}

/**
 * Resolve the absolute origin where champaign serves the runtime shim. Used by the default
 * `resolveRuntimeUrl` (editor canvas) so callers that haven't been updated still work. Published
 * pages must override `resolveRuntimeUrl` — fetching from champaign is cross-origin from a
 * customer domain and the global CORS allowlist excludes them.
 */
function champaignAbsoluteOrigin(): string {
  const domain = champaignUrlDomain();
  const protocol = typeof window !== 'undefined' ? window.location.protocol : 'https:';
  return domain.startsWith('//') ? `${protocol}${domain}` : domain;
}

const defaultResolveRuntimeUrl = (specifier: string): string => `${champaignAbsoluteOrigin()}${specifier}`;

/**
 * Wrap any subtree that may render `custom-component` nodes. This provider:
 *   1. Registers the host React singleton + a `loadComponent(slug)` dispatcher on
 *      `globalThis.__mutinyHostRuntime` — backing the runtime shim module at
 *      `/__mutiny/runtime/v1/react.js`.
 *   2. Owns a sourceHash-keyed module cache + blob-URL cache so each artifact loads once across
 *      the page even if multiple slugs reference it.
 *   3. Exposes the active filesystem manifest + theme + override context to descendants.
 *
 * Mount the provider as high in the tree as possible — child `CustomComponent` nodes assume it's
 * present. If omitted, those nodes throw with a clear message during render.
 */
export function CustomComponentProvider({
  filesystem,
  theme,
  overrideVariables,
  isEditor = false,
  resolveCompiledUrl,
  resolveRuntimeUrl = defaultResolveRuntimeUrl,
  onRenderError,
  preloadedModules,
  children,
}: CustomComponentProviderProps): React.ReactElement {
  const filesystemRef = useRef(filesystem);
  filesystemRef.current = filesystem;
  const resolveCompiledUrlRef = useRef(resolveCompiledUrl);
  resolveCompiledUrlRef.current = resolveCompiledUrl;
  const resolveRuntimeUrlRef = useRef(resolveRuntimeUrl);
  resolveRuntimeUrlRef.current = resolveRuntimeUrl;
  const onRenderErrorRef = useRef(onRenderError);
  onRenderErrorRef.current = onRenderError;

  // One loader per provider mount so per-sourceHash caches survive prop swaps.
  const loaderRef = useRef<CustomComponentLoader>();
  if (!loaderRef.current) {
    loaderRef.current = createCustomComponentLoader({
      resolveCompiledUrl: (sourceHash) => resolveCompiledUrlRef.current(sourceHash),
      resolveRuntimeUrl: (specifier) => resolveRuntimeUrlRef.current(specifier),
      getManifest: () => ({
        slugToCurrentSourceHash: filesystemRef.current?.slugToCurrentSourceHash ?? {},
      }),
      ensureRuntimeLibraries: ensureRuntimeLibrariesRegistered,
    });
  }
  const loader = loaderRef.current;
  // Dedup identical error tuples until the filesystem manifest changes (agent did something new).
  const reportedSignaturesRef = useRef<Set<string>>();
  if (!reportedSignaturesRef.current) reportedSignaturesRef.current = new Set();

  useEffect(() => {
    reportedSignaturesRef.current = new Set();
  }, [filesystem]);

  const preloadedRef = useRef<ReadonlyMap<string, LoadedCustomComponentModule> | undefined>();
  preloadedRef.current = preloadedModules;

  const getPreloadedModule = useCallback(
    (sourceHash: string): LoadedCustomComponentModule | null =>
      preloadedRef.current?.get(sourceHash) ?? loader.getResolvedByHash(sourceHash),
    [loader],
  );

  const loadModuleByHash = useCallback(
    (sourceHash: string): Promise<LoadedCustomComponentModule> => loader.loadByHash(sourceHash),
    [loader],
  );

  const reportRenderError = useCallback((payload: CustomComponentRenderErrorPayload) => {
    const signature = `${payload.slug ?? ''}|${payload.sourceHash}|${payload.errorName}|${payload.errorMessage}`;
    if (reportedSignaturesRef.current!.has(signature)) return;
    reportedSignaturesRef.current!.add(signature);
    onRenderErrorRef.current?.(payload);
  }, []);

  // Catch event-handler throws + unhandled rejections from our blob URLs that bypass the boundary.
  useEffect(() => {
    const blobUrlToHash = loader.blobUrlToSourceHash;
    const matchBlobUrl = (text: string | undefined | null): string | null => {
      if (!text) return null;
      for (const blobUrl of blobUrlToHash.keys()) {
        if (text.includes(blobUrl)) return blobUrlToHash.get(blobUrl) ?? null;
      }
      return null;
    };

    const lookupSlug = (sourceHash: string): string | null => {
      const fs = filesystemRef.current;
      if (!fs) return null;
      for (const [slug, hash] of Object.entries(fs.slugToCurrentSourceHash)) {
        if (hash === sourceHash) return slug;
      }
      return null;
    };

    const reportFromError = (error: unknown, fallbackFilename?: string | null): boolean => {
      const stack = error instanceof Error ? error.stack : undefined;
      const sourceHash = matchBlobUrl(stack) ?? matchBlobUrl(fallbackFilename);
      if (!sourceHash) return false;
      const errObj = error instanceof Error ? error : new Error(String(error));
      reportRenderError({
        slug: lookupSlug(sourceHash),
        sourceHash,
        errorName: errObj.name || 'Error',
        errorMessage: errObj.message || String(error),
        stack: errObj.stack,
      });

      console.error(`[custom-component:${sourceHash}] unhandled error:`, errObj);
      return true;
    };

    const onWindowError = (event: ErrorEvent) => {
      if (reportFromError(event.error ?? event.message, event.filename)) {
        event.preventDefault();
      }
    };
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (reportFromError(event.reason, null)) {
        event.preventDefault();
      }
    };

    window.addEventListener('error', onWindowError);
    window.addEventListener('unhandledrejection', onUnhandledRejection);
    return () => {
      window.removeEventListener('error', onWindowError);
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
    };
  }, [reportRenderError, loader]);

  // The runtime's `loadComponent(slug)` is the entry point compiled artifacts call when they have
  // a `components/<slug>` bare import. The shared loader resolves the slug through the manifest.
  const loadComponentBySlug = useCallback(
    (slug: string): Promise<LoadedCustomComponentModule> => loader.loadBySlug(slug),
    [loader],
  );

  // Register host runtime as early as possible — before children render. Doing it in useEffect is
  // too late: children may trigger a dynamic import during their first render, and the artifact's
  // shim would throw. useMemo runs during render, before descendants commit.
  useMemo(() => {
    registerHostRuntime({ React, loadComponent: loadComponentBySlug });
  }, [loadComponentBySlug]);

  useEffect(() => {
    registerHostRuntime({ React, loadComponent: loadComponentBySlug });
  }, [loadComponentBySlug]);

  const value = useMemo<CustomComponentContextValue>(
    () => ({
      filesystem,
      theme,
      overrideVariables,
      isEditor,
      loadModuleByHash,
      getPreloadedModule,
      onRenderError: reportRenderError,
    }),
    [filesystem, theme, overrideVariables, isEditor, loadModuleByHash, getPreloadedModule, reportRenderError],
  );

  return <CustomComponentContext.Provider value={value}>{children}</CustomComponentContext.Provider>;
}

export function useCustomComponentContext(): CustomComponentContextValue | null {
  return useContext(CustomComponentContext);
}
